<template>
   <Modal
    name="CHANGE_APPOINTMENT_STATUS_MODAL"
    :title="$t('appointment.adminActions.changeStatus.action')"
    actions
  >
    <template v-slot:body>
      <v-progress-linear
        class="mt-5 mb-7"
        v-show="isLoading"
        indeterminate
        rounded
      />

      <div v-show="!isLoading">
        <v-alert class="error lighten-5 mb-6" v-if="warningMessage">
          <p class="body-2 error--text mb-0">
            {{ warningMessage }}
          </p>
        </v-alert>

        <v-alert class="info lighten-5 mb-6" v-if="reopenInfoMessage">
          <p class="body-2 info--text mb-0">
            {{ reopenInfoMessage }}
          </p>
        </v-alert>

        <v-form ref="appointmentFormChangeStatus" lazy-validation>
          <v-autocomplete
            v-model="status"
            :label="$t('appointment.adminActions.changeStatus.selectStatus')"
            :no-data-text="$t('appointment.filters.noData')"
            :items="appointmentStatus"
            autocomplete="one-time-code"
            hide-details="auto"
            item-value="value"
            item-text="name"
            clearable
            outlined
            small
          />
        </v-form>
      </div>
    </template>
    <template v-slot:actions>
       <v-btn
        @click="closeAllModals"
        :disabled="isLoading || isUpdatingStatus"
        elevation="0"
        class="mr-5"
        large
        text
      >
        {{ $t('appointment.buttons.cancel') }}
      </v-btn>
       <v-btn
        @click="validate"
        :loading="isLoading || isUpdatingStatus"
        :disabled="disableStatusChangeButton"
        color="primary"
        elevation="0"
        large
      >
        {{ $t('appointment.adminActions.changeStatus.action') }}
      </v-btn>
    </template>
  </Modal>
</template>
<script>
import { mapGetters, mapMutations } from 'vuex'
import { required } from '@/util/errorHandling'
import { canReopenAppointment } from '@/components/appointment/menuAdminAppointment'
import Modal from '@/components/app/Modal'

export default {
  name: 'AppointmentChangeStatus',
  components: {
    Modal
  },

  data() {
    return {
      status: null,
      isUpdatingStatus: false,
      rules: {
        required
      },
    }
  },

  watch: {
    activeModal() {
      this.status = null
    }
  },

  computed: {
    ...mapGetters(['appointment', 'isLoading', 'activeModal']),

    isAdminOrPartner() {
      const roles = localStorage.getItem('access_roles')
      const userRoles = roles?.split(',') ?? []
      return userRoles.includes('ROLE_ADMIN') || userRoles.includes('ROLE_PARTNER')
    },

    isRealEstate() {
      const roles = localStorage.getItem('access_roles')
      const userRoles = roles?.split(',') ?? []
      return userRoles.includes('ROLE_REAL_ESTATE') || userRoles.includes('ROLE_REAL_ESTATE_OPERATOR')
    },

    appointmentStatus() {
      if (this.isAdminOrPartner) {
        return [
          { value: 'OPEN', name: this.$t('appointment.status.OPEN') },
          { value: 'SCHEDULED', name: this.$t('appointment.status.SCHEDULED') },
          { value: 'DRAFT', name: this.$t('appointment.status.DRAFT') },
        ]
      }

      if (this.isRealEstate) {
        if (this.isAppointmentDone) {
          if (!this.canReopen) return []

          return [
            { value: 'SCHEDULED', name: this.$t('appointment.status.SCHEDULED') },
          ]
        }

        return [
          { value: 'OPEN', name: this.$t('appointment.status.OPEN') },
          { value: 'DRAFT', name: this.$t('appointment.status.DRAFT') },
        ]
      }

      return []
    },

    canReopen() {
      return canReopenAppointment(this.appointment)
    },

    isAppointmentDone() {
      return this.appointment?.status === 'DONE'
    },

    isReopeningAppointment() {
      return this.isAppointmentDone && this.status === 'SCHEDULED'
    },

    reopenInfoMessage() {
      if (!this.isReopeningAppointment || this.warningMessage) return null

      return this.$t('appointment.adminActions.changeStatus.reopenInfoMessage')
    },

    warningMessage() {
      if (!this.status) return null

      if (this.isRealEstate) {
        const current = this.appointment?.status
        const next = this.status

        if (current === 'DONE') {
          if (next !== 'SCHEDULED' || !this.appointment?.host) {
            return this.$t('appointment.adminActions.changeStatus.scheduledWarningMessage', {
              status: this.$t('appointment.status.SCHEDULED')
            })
          }

          return null
        }

        const isDraftToOpen = current === 'DRAFT' && next === 'OPEN'
        const isOpenToDraft = current === 'OPEN' && next === 'DRAFT'

        if (isDraftToOpen || isOpenToDraft) return null

        return this.$t('appointment.adminActions.changeStatus.draftWarningMessage', {
          status: this.$t(`appointment.status.${next}`)
        })
      }

      if (this.status === 'OPEN' && !this.canSelectOpen) {
        return this.$t('appointment.adminActions.changeStatus.openWarningMessage', {
          status: this.$t(`appointment.status.${this.status}`)
        })
      }

      if (this.status === 'SCHEDULED' && !this.canSelectScheduled) {
        return this.$t('appointment.adminActions.changeStatus.scheduledWarningMessage', {
          status: this.$t(`appointment.status.${this.status}`)
        })
      }

      if (this.status === 'DRAFT' && !this.canSelectDraft) {
        return this.$t('appointment.adminActions.changeStatus.draftWarningMessage', {
          status: this.$t(`appointment.status.${this.status}`)
        })
      }

      return null
    },

    disableStatusChangeButton() {
      if (!this.status) return true
      return !!this.warningMessage
    },

    canSelectOpen() {
      return this.appointment.status !== 'OPEN'
    },

    canSelectScheduled() {
      return ['DONE', 'CANCELED'].includes(this.appointment.status) && this.appointment.host
    },

    canSelectDraft() {
      return ['OPEN', 'REJECTED', 'CANCELED'].includes(this.appointment.status) && !this.appointment.host && !this.appointment.group_code
    },
  },

  methods: {
    ...mapMutations(['closeAllModals']),

    validate() {
      if (this.$refs.appointmentFormChangeStatus.validate()) {
        this.isUpdatingStatus = true

        const body = {
          appointment_id: this.appointment.id,
          status: this.status,
          started: false,
        }

        const action = this.isAdminOrPartner
          ? 'adminAppointmentChangeStatus/changeStatus'
          : 'webAppointmentChangeStatus/changeStatus'

        this.$store
          .dispatch(action, body)
          .then(() => {
            if (this.$parent?.refreshMap) this.$parent?.refreshMap()
            if (this.$parent?.refreshAppointments) this.$parent?.refreshAppointments()
          })
          .then(() => this.closeAllModals())
          .finally(() => {
            this.isUpdatingStatus = false
            this.status = null
          })
      }
    },
  },
}
</script>
